/**
 * Coral & Amber — Radio Ibnul Qoyyim design system.
 *
 * Canonical color and layout values for TypeScript (PWA manifest, canvas, scripts).
 * CSS custom properties live in `styles/tokens.css` and must stay in sync
 * (enforced by `design-tokens.test.ts`).
 */
export const FA_DESIGN_SYSTEM = "fern-amber" as const;

/** Raw hex / length values — source of truth for non-CSS consumers */
export const faColor = {
  background: "#000000",
  surface: "#000000",
  moduleBg: "#1a1e28",
  surfaceContainer: "#1a1e28",
  surfaceContainerHigh: "#252a36",
  surfaceContainerHighest: "#353b48",
  surfaceContainerLow: "#141618",
  surfaceContainerLowest: "#0e0e0e",
  primary: "#ffd333",
  primaryContainer: "#b89200",
  onPrimary: "#1a1400",
  onSurface: "#eceae8",
  onSurfaceVariant: "#c8c6c4",
  onBackground: "#eceae8",
  outline: "#a8a6a4",
  outlineVariant: "#5c5a58",
  tertiary: "#d4d2d1",
  tertiaryFixedDim: "#d4d2d1",
  amber: "#ffd333",
  onAmber: "#1a1400",
  brand: "#d3706f",
  brandContainer: "#a85857",
  onBrand: "#fff5f4",
  live: "#d3706f",
  error: "#e84855",
  errorContainer: "#9c2a32",
  onError: "#fff5f3",
} as const;

export type FaColorKey = keyof typeof faColor;

export const faLayout = {
  marginMobile: "20px",
  marginDesktop: "48px",
  sectionGap: "12px",
  pageGutterInline: "16px",
  pageMaxWidth: "640px",
  navHeight: "80px",
  touchTarget: "48px",
  sidebarWidth: "240px",
} as const;

export const faFontFamily = {
  sans: "'Hanken Grotesk', system-ui, sans-serif",
  arabic: "'Noto Serif', serif",
  icons: "Material Symbols Outlined",
} as const;

export const faRadius = {
  sm: "4px",
  md: "8px",
  lg: "12px",
  xl: "16px",
  pill: "999px",
  full: "9999px",
} as const;

/**
 * Motion durations and easings — the vocabulary every animated surface draws from.
 *
 * `tokens.css` collapses the durations to `0ms` under `prefers-reduced-motion`, so CSS
 * transitions written against `var(--fa-motion-*)` become no-ops for free. Svelte
 * `transition:` directives cannot read CSS variables; those go through `motionDuration()`
 * in `motion.ts`, which reads the same numbers from here.
 */
export const faMotion = {
  /** Press feedback, icon fill — fast enough to feel like touch response. */
  instant: 90,
  /** Hover, colour, chips. */
  fast: 150,
  /** Screen change, expand/collapse, sheets. */
  base: 220,
  /** Backdrops, artwork crossfade. */
  slow: 320,
} as const;

export type FaMotionKey = keyof typeof faMotion;

/** CSS easing curves, mirrored in `tokens.css`. */
export const faEasing = {
  /** Entering — decelerate into place. */
  out: "cubic-bezier(0.2, 0, 0, 1)",
  /** Exiting — accelerate away. */
  in: "cubic-bezier(0.3, 0, 1, 1)",
  /** Both directions. */
  standard: "cubic-bezier(0.2, 0, 0, 1)",
} as const;

/** CSS custom property names (`--fa-*`) */
export const faCssVar = {
  background: "--fa-background",
  surface: "--fa-surface",
  moduleBg: "--fa-module-bg",
  surfaceContainer: "--fa-surface-container",
  surfaceContainerHigh: "--fa-surface-container-high",
  surfaceContainerHighest: "--fa-surface-container-highest",
  surfaceContainerLow: "--fa-surface-container-low",
  surfaceContainerLowest: "--fa-surface-container-lowest",
  primary: "--fa-primary",
  primaryContainer: "--fa-primary-container",
  onPrimary: "--fa-on-primary",
  onSurface: "--fa-on-surface",
  onSurfaceVariant: "--fa-on-surface-variant",
  onBackground: "--fa-on-background",
  outline: "--fa-outline",
  outlineVariant: "--fa-outline-variant",
  tertiary: "--fa-tertiary",
  tertiaryFixedDim: "--fa-tertiary-fixed-dim",
  amber: "--fa-amber",
  onAmber: "--fa-on-amber",
  brand: "--fa-brand",
  brandContainer: "--fa-brand-container",
  onBrand: "--fa-on-brand",
  live: "--fa-live",
  error: "--fa-error",
  errorContainer: "--fa-error-container",
  onError: "--fa-on-error",
  scrim: "--fa-scrim",
  scrimMedium: "--fa-scrim-medium",
  scrimLight: "--fa-scrim-light",
  marginMobile: "--fa-margin-mobile",
  marginDesktop: "--fa-margin-desktop",
  sectionGap: "--fa-section-gap",
  pageGutterInline: "--fa-page-gutter-inline",
  pageMaxWidth: "--fa-page-max-width",
  navHeight: "--fa-nav-height",
  touchTarget: "--fa-touch-target",
  compactHeight: "--fa-compact-height",
  sidebarWidth: "--fa-sidebar-width",
  radiusSm: "--fa-radius-sm",
  radiusMd: "--fa-radius-md",
  radiusLg: "--fa-radius-lg",
  radiusXl: "--fa-radius-xl",
  radiusPill: "--fa-radius-pill",
  radiusFull: "--fa-radius-full",
  motionInstant: "--fa-motion-instant",
  motionFast: "--fa-motion-fast",
  motionBase: "--fa-motion-base",
  motionSlow: "--fa-motion-slow",
  easeOut: "--fa-ease-out",
  easeIn: "--fa-ease-in",
  easeStandard: "--fa-ease-standard",
} as const;

export type FaCssVarKey = keyof typeof faCssVar;

/** `var(--fa-primary)` etc. for inline styles and canvas fallbacks */
export function faVar(key: FaCssVarKey): string {
  return `var(${faCssVar[key]})`;
}

/** PWA manifest / meta theme-color (must match tokens.css) */
export const faPwaTheme = {
  themeColor: faColor.primary,
  backgroundColor: faColor.background,
} as const;

/** Parse `#rrggbb` to `{ r, g, b, a }` for PNG/canvas scripts */
export function hexToRgb(hex: string, alpha = 255): { r: number; g: number; b: number; a: number } {
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) {
    throw new Error(`Expected #rrggbb, got ${hex}`);
  }
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
    a: alpha,
  };
}
